import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  RowSelectionState
} from "@tanstack/react-table";
import { Image  } from "react-bootstrap";
import { ASSESTS } from "../../../../../../../constant/assest";
import CustomButton, { ButtonFont, ButtonSize, ButtonType } from "../../../../../../../component/common/CustomButton/CustomButton";
import cn from 'classnames';
import ActionButtons from "../../../../../../../component/UI/ActionButtons/ActionButtons";
import { useEffect, useMemo, useState } from "react";
import Pagination from "../../../../../../../component/UI/Pagination/Pagination";
import AddUpdateRole from "../AddUpdateRole/AddUpdateRole";
import DeleteModal from "../../../../../../../component/common/DeleteModal/DeleteModal";

type RoleRow = {
  role: string;
  permissions: string;
};

const RolesTable = () => {
    const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    const data = useMemo<RoleRow[]>(() => [
        { role: "Admin", permissions: "Full Access" },
        { role: "Editor", permissions: "Full Access" },
        { role: "Support", permissions: "Full Access" },
    ], []);

    const columns = useMemo<ColumnDef<RoleRow>[]>(() => [
    {
        header: "Role",
        accessorKey: "role",
        cell: (info) => {
        const value = info.getValue<string>();
        return (
            <span className={cn("roleBadge", value.toLowerCase())}>{value}</span>
        );
        },
    },
    {
        header: "Permissions",
        accessorKey: "permissions",
        cell: (info) => {
            const value = info.getValue<string>();
            return (
                <span className={cn("permissionBadge", value.toLowerCase())}>{info.getValue<string>()}</span>
            );
        },
    },
    {
        header: "Action",
        accessorKey: "action",
        cell: () => (
        <ActionButtons
            editBtnClick={() => setShowRoleModal(true)}
            viewBtnImage={ASSESTS.DELETE}
            viewBtnClick={() => setShowDeleteModal(true)}
        />
        ),
        enableSorting: false,
    },
    ], []);

    const [sorting, setSorting] = useState<SortingState>([]);
    const [pageSize, setPageSize] = useState(5);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const table = useReactTable({
        data,
        columns,
        state: { sorting, rowSelection },
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableRowSelection: true,
        initialState: { pagination: { pageIndex: 0, pageSize: pageSize } }
    });

    useEffect(() => {
        table.setPageSize(pageSize);
    }, [pageSize, table]);

    return (
        <>
        <div className={'tableContainer'}>
            <div className={'tableWrapper'}>
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isActionColumn = typeof header.column.columnDef.header === "string" && header.column.columnDef.header === "Action";
                                    return (
                                    <th
                                        key={header.id}
                                        onClick={header.column.getToggleSortingHandler()}
                                        style={{ 
                                            cursor: header.column.getCanSort() ? "pointer" : "default"
                                        }}
                                        className={cn(header.column.columnDef.id && `th-${header.column.columnDef.id}` , isActionColumn ? 'text-center' : undefined)}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getCanSort() && 
                                            <span className={cn('tableShorting')}>
                                                <Image 
                                                    src={header.column.getIsSorted() === "asc" ? ASSESTS.ASSENDING : header.column.getIsSorted() === "desc" ? ASSESTS.DECENDING : ASSESTS.SHORT} 
                                                    alt="Short" 
                                                />
                                            </span>
                                        }
                                    </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => {
                                    const isActionColumn = typeof cell.column.columnDef.header === "string" && cell.column.columnDef.header === "Action";

                                    return (
                                    <td 
                                        key={cell.id} 
                                        className={cn(cell.column.columnDef.id && `td-${cell.column.columnDef.id}`, isActionColumn ? 'text-center' : undefined)}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                );
                            })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination
                enteriesShows={`${table.getState().pagination.pageIndex * pageSize + 1}-${Math.min((table.getState().pagination.pageIndex + 1) * pageSize, data.length )} of ${data.length} Entries`}
                pageNumbers={Array.from({ length: table.getPageCount() }, (_, pageIndex) => (
                    <CustomButton
                        key={pageIndex}
                        onClick={() => table.setPageIndex(pageIndex)}
                        disabled={table.getState().pagination.pageIndex === pageIndex}
                        btnType={table.getState().pagination.pageIndex === pageIndex ? ButtonType.ACTIVENOSTYLE : ButtonType.NOSTYLE}
                        btnSize={ButtonSize.NORMAL}
                        btnFont={table.getState().pagination.pageIndex === pageIndex ? ButtonFont.BOLD : ButtonFont.NORMAL}
                        className={cn('pageNumberBtn', table.getState().pagination.pageIndex === pageIndex && 'activePageNumberBtn')}
                    >
                        {pageIndex + 1}
                    </CustomButton>
                ))}
                dropDownOptions={[
                    { value: "5", label: "5" },
                    { value: "10", label: "10" },
                    { value: "20", label: "20" },
                    { value: "25", label: "25" },
                ]}
                dropdownOnChange={(value) => { setPageSize(Number(value)); }}
                dropDownPlaceholder={pageSize.toString()}
            />
        </div>
        <AddUpdateRole 
            title={'New Role'}
            showModal={showRoleModal} 
            onClose={() => setShowRoleModal(false)} 
        />
        <DeleteModal 
            title={'Delete'}
            showModal={showDeleteModal}
            onClose={() => setShowDeleteModal(false)} 
            bodyText={'Are you sure want to delete this Item?'} 
            onDelete={() => setShowDeleteModal(false)}
        />
        </>
    );
};

export default RolesTable;
